import {createSelector, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {RootState} from '../../store';
import { Message, OrderDetails, OrderListItem, OrderStatusChangePayload, PlanListItem, ResourceListItem } from '../../types';
import { orderMessagesApi } from '../../services/orderMessages';
import { salesOrdersApi } from '../../services/salesOrders';
import { SalesOrderProduct, SalesOrderDetails, SalesOrderListItem } from './types';

interface SalesState {
	orderListItems: SalesOrderListItem[];
	orderDetails: SalesOrderDetails | null;
	orderResources: ResourceListItem[];
	orderMessages: Message[];
	orderProducts: SalesOrderProduct[];
	orderPlan: PlanListItem | null;
	orderCustomer: any | null; // TODO: actual type
}

const initialState: SalesState = {
	orderListItems: [],
	orderDetails: null,
	orderResources: [],
	orderMessages: [],
	orderProducts: [],
	orderPlan: null,
	orderCustomer: null,
};

export const salesSlice = createSlice({
	name: 'sales-order',
	initialState,
	reducers: {
		salesOrderListItemsSetState(state: SalesState, action) {
			state.orderListItems = action.payload;
			return state;
		},
		salesOrderDetailsSetState(state: SalesState, action) {
			const {messages, materials, products, plan, customer, ...orderDetails} = action.payload;
			state.orderDetails = orderDetails;
			state.orderMessages = messages;
			state.orderResources = materials;
			state.orderProducts = products;
			state.orderPlan = plan;
			state.orderCustomer = customer;
			return state;
		},
		salesOrderDetailsUpdateState(state: SalesState, action) {
			state.orderDetails = state.orderDetails?.id === action.payload.id ? {...state.orderDetails, ...action.payload} : state.orderDetails;
		},
		salesOrderMessagesAdd(state: SalesState, action: PayloadAction<Message>) {
			state.orderMessages = [...state.orderMessages, action.payload];
		},
		salesOrderProductAdd(state: SalesState, action: PayloadAction<SalesOrderProduct>) {
			state.orderProducts = [...state.orderProducts, action.payload];
		},
		salesOrderProductRemove(state: SalesState, action: PayloadAction<number>) {
			state.orderProducts = state.orderProducts.filter((product) => product.id !== action.payload);
		}
	},
	extraReducers: (builder) => {
		builder
			.addMatcher(salesOrdersApi.endpoints.salesOrderListPublic.matchFulfilled, (state: SalesState, action: PayloadAction<OrderListItem>) => {
				salesSlice.caseReducers.salesOrderListItemsSetState(state,	salesSlice.actions.salesOrderListItemsSetState(action.payload));
			})
			.addMatcher(salesOrdersApi.endpoints.salesOrderDetailsLoad.matchFulfilled, (state: SalesState, action: PayloadAction<OrderDetails>) => {
				salesSlice.caseReducers.salesOrderDetailsSetState(state,	salesSlice.actions.salesOrderDetailsSetState(action.payload));
			})
			.addMatcher(orderMessagesApi.endpoints.messageSubmit.matchFulfilled, (state: SalesState, action: PayloadAction<Message>) => {
				salesSlice.caseReducers.salesOrderMessagesAdd(state, salesSlice.actions.salesOrderMessagesAdd(action.payload));
			})
			.addMatcher(salesOrdersApi.endpoints.salesOrderAddExistingProduct.matchFulfilled, (state: SalesState, action: PayloadAction<SalesOrderProduct>) => {
				salesSlice.caseReducers.salesOrderProductAdd(state, salesSlice.actions.salesOrderProductAdd(action.payload));
			})
			.addMatcher(salesOrdersApi.endpoints.salesOrderAddNewProduct.matchFulfilled, (state: SalesState, action: PayloadAction<SalesOrderProduct>) => {
				salesSlice.caseReducers.salesOrderProductAdd(state, salesSlice.actions.salesOrderProductAdd(action.payload));
			})
			.addMatcher(salesOrdersApi.endpoints.salesOrderProductDelete.matchFulfilled, (state: SalesState, action: PayloadAction<{ id: number }>) => {
				salesSlice.caseReducers.salesOrderProductRemove(state, salesSlice.actions.salesOrderProductRemove(action.payload.id));
			})
			.addMatcher(salesOrdersApi.endpoints.salesOrderSubmitOffer.matchFulfilled, (state: SalesState, action: PayloadAction<OrderStatusChangePayload>) => {
				salesSlice.caseReducers.salesOrderDetailsUpdateState(state, salesSlice.actions.salesOrderDetailsUpdateState(action.payload));
			})
			.addMatcher(salesOrdersApi.endpoints.salesOrderCancelOffer.matchFulfilled, (state: SalesState, action: PayloadAction<OrderStatusChangePayload>) => {
				salesSlice.caseReducers.salesOrderDetailsUpdateState(state, salesSlice.actions.salesOrderDetailsUpdateState(action.payload));
			})
			.addMatcher(salesOrdersApi.endpoints.salesOrderRejectOrder.matchFulfilled, (state: SalesState, action: PayloadAction<OrderStatusChangePayload>) => {
				salesSlice.caseReducers.salesOrderDetailsUpdateState(state, salesSlice.actions.salesOrderDetailsUpdateState(action.payload));
			})
			.addMatcher(salesOrdersApi.endpoints.salesOrderSetStatusShipped.matchFulfilled, (state: SalesState, action: PayloadAction<OrderStatusChangePayload>) => {
				salesSlice.caseReducers.salesOrderDetailsUpdateState(state, salesSlice.actions.salesOrderDetailsUpdateState(action.payload));
			});							
	},
});

export const {
	salesOrderDetailsSetState,
} = salesSlice.actions;

export const selectSalesState = (state: RootState) => state.sales;

export const selectSalesOrdersListItems = createSelector([selectSalesState], (state) => state.orderListItems);

export const selectSalesOrderDetails = createSelector([selectSalesState], (state) => state.orderDetails);

export const selectSalesOrderMessages = createSelector([selectSalesState], (state) => state.orderMessages);

export const selectSalesOrderResources = createSelector([selectSalesState], (state) => state.orderResources);

export const selectSalesOrderProducts = createSelector([selectSalesState], (state) => state.orderProducts);

export const selectSalesOrderPlan = createSelector([selectSalesState], (state) => state.orderPlan);

export const selectSalesOrderCustomer = createSelector([selectSalesState], (state) => state.orderCustomer);

export default salesSlice.reducer;
